import { useId, useRef, useState, type PointerEvent } from "react";
import { PARCEL_POIS, PARCEL_SPACE, parcelDistance, type ParcelPosition } from "../../config/parcelSpatial";
import type { ParcelReminderState, ParcelReminderEvent } from "../../logic/parcelReminderMachine";

export function formatParcelDistance(distanceM: number) {
  return distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`;
}
const toSvg = (p: ParcelPosition) => ({ x: (p.x - PARCEL_SPACE.minX) * PARCEL_SPACE.scale, y: (p.y - PARCEL_SPACE.minY) * PARCEL_SPACE.scale });

export function ParcelSpatialDiagram({ model, onEvent }: { model: ParcelReminderState; onEvent: (event: ParcelReminderEvent) => void }) {
  const id = useId().replace(/:/g, "");
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ id: number; offset: ParcelPosition } | null>(null);
  const [dragging, setDragging] = useState(false);
  const car = toSvg(model.vehiclePosition);
  function pointerPosition(event: PointerEvent): ParcelPosition | null {
    const svg = svgRef.current;
    const matrix = svg?.getScreenCTM();
    if (!svg || !matrix) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(matrix.inverse());
    return { x: local.x / PARCEL_SPACE.scale + PARCEL_SPACE.minX, y: local.y / PARCEL_SPACE.scale + PARCEL_SPACE.minY };
  }
  function move(event: PointerEvent) {
    if (!drag.current || drag.current.id !== event.pointerId) return;
    const point = pointerPosition(event);
    if (point) onEvent({ type: "POSITION", position: { x: point.x - drag.current.offset.x, y: point.y - drag.current.offset.y } });
  }
  function finish(event: PointerEvent) {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    setDragging(false);
    if (svgRef.current?.hasPointerCapture(event.pointerId)) svgRef.current.releasePointerCapture(event.pointerId);
  }
  return <div className={`parcel-spatial${dragging ? " is-dragging" : ""}`}>
    <div className="parcel-spatial__instructions" id={`${id}-help`}><strong>拖动车辆，体验进圈提醒</strong><span>移动即切回 D 档 · 选中车辆后也可用方向键微调</span><span className="parcel-spatial__mobile-hint">左右滑动示意图，查看家和公司</span></div>
    <svg ref={svgRef} viewBox="0 0 1100 520" role="group" aria-label="家和公司双地点车辆位置模拟" onPointerMove={move} onPointerUp={event => { move(event); finish(event); }} onPointerCancel={finish} onLostPointerCapture={finish}>
      <defs>
        <linearGradient id={`${id}-car`} x1="0" x2="1"><stop stopColor="#e9eeeb"/><stop offset=".4" stopColor="#fff"/><stop offset="1" stopColor="#bacfc4"/></linearGradient>
        <pattern id={`${id}-grid`} width="50" height="50" patternUnits="userSpaceOnUse"><path d="M 50 0 H 0 V 50" fill="none" stroke="#e4ede8" strokeWidth=".6"/></pattern>
      </defs>
      <rect x="0" y="0" width="1100" height="520" fill={`url(#${id}-grid)`}/>
      <path d="M 300 260 H 800" stroke="#d4dfd9" strokeWidth="2" strokeDasharray="6 8"/>
      <text x="550" y="224" textAnchor="middle" className="parcel-spatial__gap">范围圈间隔 1 km</text>
      {(Object.keys(PARCEL_POIS) as Array<keyof typeof PARCEL_POIS>).map(poi => {
        const center = toSvg(PARCEL_POIS[poi]);
        const inside = parcelDistance(model.vehiclePosition, poi) <= 2000;
        const color = poi === "home" ? "#4f9877" : "#638dc0";
        return <g key={poi} aria-label={`${PARCEL_POIS[poi].label}：2公里提醒范围、500米停车提醒与快递查询范围`}>
          <circle cx={center.x} cy={center.y} r="200" fill={color} fillOpacity={inside ? ".09" : ".035"} stroke={color} strokeWidth={inside ? "2" : "1.3"} strokeDasharray="6 6"/>
          <circle cx={center.x} cy={center.y} r="50" fill={color} fillOpacity=".13" stroke={color} strokeOpacity=".55" strokeWidth="1.5"/>
          <text x={center.x} y="42" textAnchor="middle" className="parcel-spatial__outer-label" fill={color}>{PARCEL_POIS[poi].label} · 2 km 提醒范围</text>
          <path d={`M ${center.x + 40} 230 L ${center.x + 90} 192 H ${center.x + 172}`} fill="none" stroke={color} strokeOpacity=".5"/>
          <text x={center.x + 98} y="184" className="parcel-spatial__inner-label">500 m 停车提醒</text>
          <g transform={`translate(${center.x - 22} 178)`}>
            <rect width="44" height="44" rx="13" fill="white" stroke={color} strokeOpacity=".25"/>
            {poi === "home" ? <g fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round"><path d="M 10 21 L 22 10 L 34 21 V 35 H 10 Z"/><path d="M 18 35 V 25 H 26 V 35"/></g>
              : <g fill="none" stroke={color} strokeWidth="2"><path d="M 12 10 H 32 V 35 H 12 Z M 19 35 V 28 H 25 V 35 M 17 16 H 20 M 25 16 H 28 M 17 22 H 20 M 25 22 H 28"/></g>}
          </g>
          <text x={center.x} y="164" textAnchor="middle" className="parcel-spatial__poi-label">{PARCEL_POIS[poi].label}</text>
          <path d={`M ${center.x} 226 V 254`} stroke={color}/>
          <circle cx={center.x} cy="260" r="4" fill={color}/>
          <circle cx={center.x + 30} cy="260" r="4" fill="#ba9864" stroke="white" strokeWidth="2"/>
          <path d={`M ${center.x + 34} 265 L ${center.x + 85} 323 H ${center.x + 112}`} fill="none" stroke="#c3ad88"/>
          <g transform={`translate(${center.x + 120} 308)`} fill="#faf3e7" stroke="#a88d63" strokeWidth="1.6" strokeLinejoin="round"><path d="M 0 7 L 12 0 L 24 7 V 21 L 12 28 L 0 21 Z"/><path d="M 0 7 L 12 14 L 24 7 M 12 14 V 28 M 6 3.5 L 18 10.5" fill="none"/></g>
          <text x={center.x + 132} y="360" textAnchor="middle" className="parcel-spatial__parcel-label">快递点 · 距{PARCEL_POIS[poi].label} 300 m</text>
        </g>;
      })}
      <g className="parcel-spatial__vehicle" transform={`translate(${car.x} ${car.y})`} role="button" tabIndex={0} aria-label={`可拖动车辆，距家${formatParcelDistance(parcelDistance(model.vehiclePosition, "home"))}，距公司${formatParcelDistance(parcelDistance(model.vehiclePosition, "company"))}，${model.gear}档`} aria-describedby={`${id}-help`}
        onPointerDown={event => {
          if (!event.isPrimary || event.button !== 0) return;
          const point = pointerPosition(event);
          if (!point) return;
          event.preventDefault();
          event.currentTarget.focus();
          drag.current = { id: event.pointerId, offset: { x: point.x - model.vehiclePosition.x, y: point.y - model.vehiclePosition.y } };
          svgRef.current?.setPointerCapture(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={event => {
          const directions: Record<string, ParcelPosition> = { ArrowLeft: {x: -1, y: 0}, ArrowRight: {x: 1, y: 0}, ArrowUp: {x: 0, y: -1}, ArrowDown: {x: 0, y: 1} };
          const direction = directions[event.key];
          if (!direction) return;
          event.preventDefault();
          const step = event.shiftKey ? 500 : 100;
          onEvent({ type: "POSITION", position: { x: model.vehiclePosition.x + direction.x * step, y: model.vehiclePosition.y + direction.y * step } });
        }}>
        <circle className="parcel-spatial__drag-target" r="33" fill="white" fillOpacity=".65" stroke="#78a28e" strokeDasharray="3 4"/>
        <g transform="rotate(60) scale(1.45)">
          <ellipse cx="1" cy="2" rx="11" ry="19" fill="#486957" opacity=".15"/>
          <path d="M -7 -15 Q 0 -19 7 -15 L 9 -9 V 12 Q 8 17 4 18 H -4 Q -8 17 -9 12 V -9 Z" fill={`url(#${id}-car)`} stroke="#527d66" strokeWidth="1"/>
          <path d="M -6 -9 Q 0 -12 6 -9 L 5 -3 H -5 Z" fill="#729285" stroke="#5c7d6e" strokeWidth=".5"/>
          <path d="M -5 7 H 5 L 6 12 Q 0 15 -6 12 Z" fill="#809b8e"/>
          <path d="M -5 -1 H 5 V 5 H -5 Z" fill="#d5e1d9" stroke="#90ad9b" strokeWidth=".4"/>
          <path d="M -7 -11 V -6 M 7 -11 V -6" stroke="#fdfcf0" strokeWidth="1.4"/>
          <path d="M -7 13 V 15 M 7 13 V 15" stroke="#bc8882" strokeWidth="1.2"/>
          <path d="M -11 -4 H -9 M 9 -4 H 11" stroke="#63816e" strokeWidth="1.8" strokeLinecap="round"/>
        </g>
        <text y="53" textAnchor="middle" className="parcel-spatial__vehicle-label">拖动车辆 · {model.gear}档</text>
      </g>
      <path d="M 34 492 H 84 M 34 487 V 497 M 84 487 V 497" stroke="#90a999" strokeWidth="1.5"/>
      <text x="94" y="497" className="parcel-spatial__scale">500 m</text>
      <text x="1066" y="497" textAnchor="end" className="parcel-spatial__scale">家与公司相距 5 km · 等比例空间示意</text>
    </svg>
  </div>;
}
